import React, { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { TagIcon, XMarkIcon } from "@heroicons/react/24/outline";
import api from "../services/api";
import TagBadge from "./TagBadge";

export default function TagAssignmentDialog({ element, tags, onClose, onSaved, onManageTags }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setSelectedIds((element?.Tags || []).map((tag) => Number(tag.TagID)));
    setError("");
  }, [element]);

  const toggleTag = (tagId) => {
    setSelectedIds((current) => current.includes(tagId)
      ? current.filter((id) => id !== tagId)
      : [...current, tagId]);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!element?.StockageElementID) return;
    try {
      setSaving(true);
      setError("");
      await api.put(`/stockage/${element.StockageElementID}/tags`, { TagIDs: selectedIds });
      await onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Impossible d’enregistrer les tags.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(element)} onClose={saving ? () => {} : onClose} className="relative z-50">
      <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg overflow-hidden rounded-2xl border border-sky-500/10 bg-white shadow-2xl shadow-slate-950/20 dark:bg-slate-950">
          <div className="flex items-start justify-between gap-4 border-b border-sky-500/10 bg-gradient-to-r from-sky-500/15 via-blue-500/10 to-transparent px-6 py-5">
            <div>
              <DialogTitle className="text-xl font-black text-slate-950 dark:text-white">Tags du fichier</DialogTitle>
              <p className="mt-1 truncate text-sm font-semibold text-slate-500 dark:text-slate-400">{element?.Nom}</p>
            </div>
            <button type="button" onClick={onClose} disabled={saving} className="inline-flex size-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-500/10 dark:text-slate-300" title="Fermer"><XMarkIcon className="size-5" /></button>
          </div>

          <form onSubmit={save} className="grid gap-5 p-6">
            {tags.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 p-5 text-center dark:border-slate-700">
                <TagIcon className="mx-auto size-8 text-slate-400" />
                <p className="mt-2 text-sm font-semibold text-slate-500 dark:text-slate-400">Crée d’abord un tag personnel.</p>
                <button type="button" onClick={onManageTags} className="mt-3 text-sm font-bold text-sky-600 hover:text-sky-700 dark:text-sky-300">Gérer mes tags</button>
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {tags.map((tag) => {
                  const selected = selectedIds.includes(Number(tag.TagID));
                  return (
                    <label key={tag.TagID} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${selected ? "border-sky-400 bg-sky-500/10" : "border-slate-200/70 hover:bg-slate-500/5 dark:border-slate-800"}`}>
                      <input type="checkbox" checked={selected} onChange={() => toggleTag(Number(tag.TagID))} className="size-4 accent-sky-500" />
                      <TagBadge tag={tag} className="min-w-0" />
                    </label>
                  );
                })}
              </div>
            )}

            {error && <p className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <button type="button" onClick={onManageTags} disabled={saving} className="rounded-xl border border-sky-300/40 px-5 py-3 text-sm font-bold text-sky-700 hover:bg-sky-500/10 dark:text-sky-200">Gérer mes tags</button>
              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-slate-300/60 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-500/10 dark:border-slate-700 dark:text-slate-200">Annuler</button>
                <button type="submit" disabled={saving} className="rounded-xl bg-sky-500 px-5 py-3 text-sm font-bold text-white hover:bg-sky-600 disabled:opacity-60">{saving ? "Enregistrement..." : "Enregistrer"}</button>
              </div>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
